import { ref, watchEffect } from 'vue'

import type { HttpEndpoint, HttpError } from '@backmarket/http-api'
import {
  addProductsToCart,
  addToCart as addToCartAPI,
} from '@backmarket/http-api/src/api-specs-checkout/cart/cart'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useHttpFetch as useHttpFetchV2 } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

export function useAddToCart() {
  const idBeingAddedToCart = ref<string>()
  const addingToCart = ref<'pending' | 'idle' | 'success' | 'error'>('idle')
  const errorAddingToCart = ref<HttpError | null>()
  const { trackErrors } = useTracking()
  const { fetchUser } = useUserStore()
  const $httpFetch = useHttpFetchV2()

  function useCartAPIFetch(
    cartAPIEndpoint: HttpEndpoint<unknown>,
    body: { listings: { listing_id: string }[] } | { listing_id: string },
  ) {
    const { execute, status, error } = useHttpFetch(cartAPIEndpoint, {
      immediate: false,
      body,
    })

    watchEffect(() => {
      addingToCart.value = status?.value
      errorAddingToCart.value = error?.value
      if (status.value === 'error' && error.value) {
        trackErrors({
          zone: 'bundle_add_to_cart',
          message: error.value.message,
        })
      }
    })

    return execute()
  }

  function addToCart(listingId: string) {
    idBeingAddedToCart.value = listingId

    return useCartAPIFetch(addToCartAPI, { listing_id: listingId }).then(() => {
      fetchUser()
    })
  }

  async function addAllToCart(listingIds: string[]) {
    return useCartAPIFetch(addProductsToCart, {
      listings: listingIds.map((id) => ({ listing_id: id })),
    }).then(async () => {
      await fetchUser()
    })
  }

  async function addFlashSalesDiscountToCart(
    listingPublicId: string,
    discountCode: string,
  ) {
    await $httpFetch('/bm/cart/services', {
      method: 'POST',
      body: {
        type: 'PROMO_CODE',
        listingPublicId,
        promoCodeToken: discountCode,
      },
    })
  }

  async function addPartnerPromoCodeToCart(
    listingPublicId: string,
    partnerPromoCode: GetProductResponse['includedServiceOffers']['partnerPromoCodes'][number],
  ) {
    await $httpFetch('/bm/cart/services', {
      method: 'POST',
      body: {
        type: 'PARTNER_PROMO_CODE',
        listingPublicId,
        offerId: partnerPromoCode.id,
        partnerName: partnerPromoCode.partnerName,
      },
    })
  }

  async function addMobilePlanToCart(
    listingPublicId: string,
    mobilePlanOfferId: string,
  ) {
    await $httpFetch('/bm/cart/services', {
      method: 'POST',
      body: {
        type: 'MOBILE_PLAN',
        listingPublicId,
        offerId: mobilePlanOfferId,
      },
    })
  }

  return {
    addToCart,
    addAllToCart,
    idBeingAddedToCart,
    addingToCart,
    errorAddingToCart,
    addFlashSalesDiscountToCart,
    addMobilePlanToCart,
    addPartnerPromoCodeToCart,
  }
}
